/**
 * HVAC subservice flow: problem selection, branch steps, and tail steps.
 * Kept to display saved requests from the earlier HVAC wizard.
 */
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import HearingIcon from '@mui/icons-material/Hearing';
import AirIcon from '@mui/icons-material/Air';
import BuildIcon from '@mui/icons-material/Build';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HomeIcon from '@mui/icons-material/Home';
import SpeedIcon from '@mui/icons-material/Speed';
import BlockIcon from '@mui/icons-material/Block';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import HandymanIcon from '@mui/icons-material/Handyman';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import ConstructionIcon from '@mui/icons-material/Construction';
import SearchIcon from '@mui/icons-material/Search';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import CheckIcon from '@mui/icons-material/Check';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import TimerIcon from '@mui/icons-material/Timer';
import HistoryIcon from '@mui/icons-material/History';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SevereColdIcon from '@mui/icons-material/SevereCold';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SensorsIcon from '@mui/icons-material/Sensors';
import SettingsIcon from '@mui/icons-material/Settings';
import WifiIcon from '@mui/icons-material/Wifi';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TuneIcon from '@mui/icons-material/Tune';
import GasMeterIcon from '@mui/icons-material/GasMeter';
import CabinIcon from '@mui/icons-material/Cabin';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import KitchenIcon from '@mui/icons-material/Kitchen';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HvacIcon from '@mui/icons-material/Hvac';
import BoltIcon from '@mui/icons-material/Bolt';

// ─── Step 1: Problem select ────────────────────────────────────────────────

const HVAC_PROBLEMS = [
  { id: 'ac-not-cooling', label: "My AC isn't cooling", icon: AcUnitIcon },
  { id: 'heat-not-working', label: "My heat isn't working", icon: WhatshotIcon },
  { id: 'smell-sound', label: 'Something smells or sounds wrong', icon: HearingIcon },
  { id: 'airflow-vents', label: 'My airflow or vents seem off', icon: AirIcon },
  { id: 'tune-up', label: 'Schedule a tune-up or maintenance visit', icon: BuildIcon },
  { id: 'thermostat-install', label: 'Install or replace a thermostat', icon: ThermostatIcon },
  { id: 'ac-install', label: 'Install a new AC unit', icon: AddCircleOutlineIcon },
  { id: 'furnace-install', label: 'Install a new furnace or heating system', icon: LocalFireDepartmentIcon },
  /** Wizard-only: not a registrable subservice in Cosmos — routes users to matching pros. */
  { id: 'emergency', label: "It's an emergency - no heat or AC", icon: ReportProblemIcon, urgent: true },
  { id: 'not-sure', label: "I'm not sure - I need a diagnosis", icon: HelpOutlineIcon },
];

// ─── Shared urgency ─────────────────────────────────────────────────────────

const URGENCY_OPTIONS = [
  { id: 'asap', label: 'As soon as possible', icon: WarningAmberIcon, urgent: true },
  { id: 'today', label: 'Today', icon: TodayIcon },
  { id: 'few-days', label: 'Within the next few days', icon: DateRangeIcon },
  { id: 'just-quotes', label: 'Just getting quotes', icon: RequestQuoteIcon },
];

// ─── AC Not Cooling ─────────────────────────────────────────────────────────

const AC_SYMPTOM_OPTIONS = [
  { id: 'blowing-warm', label: 'Blowing warm or room-temperature air', icon: WhatshotIcon },
  { id: 'not-turning-on', label: "AC unit won't turn on at all", icon: PowerOffIcon },
  { id: 'runs-wont-cool', label: 'Runs but never reaches set temperature', icon: ThermostatIcon },
  { id: 'short-cycling', label: 'Turns on and off frequently (short cycling)', icon: RestartAltIcon },
  { id: 'freezing-up', label: 'Unit is freezing up or icing over', icon: AcUnitIcon },
  { id: 'leaking-water', label: 'Leaking water inside or outside', icon: WaterDropIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const AC_AGE_OPTIONS = [
  { id: 'under-5', label: 'Less than 5 years', icon: NewReleasesIcon },
  { id: '5-to-10', label: '5-10 years', icon: TimerIcon },
  { id: '10-to-15', label: '10-15 years', icon: HistoryIcon },
  { id: 'over-15', label: 'Over 15 years', icon: HistoryIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const AC_FILTER_OPTIONS = [
  { id: 'recently-changed', label: 'Yes, changed within the last month', icon: CheckIcon },
  { id: 'few-months', label: "It's been a few months", icon: TimerIcon },
  { id: 'no-idea', label: "I haven't checked / not sure", icon: HelpOutlineIcon },
];

// ─── Heat Not Working ───────────────────────────────────────────────────────

const HEAT_SYMPTOM_OPTIONS = [
  { id: 'blowing-cold', label: 'Blowing cold air', icon: SevereColdIcon },
  { id: 'not-turning-on', label: "Furnace / heater won't turn on", icon: PowerOffIcon },
  { id: 'runs-not-warm', label: 'Runs but house stays cold', icon: ThermostatIcon },
  { id: 'short-cycling', label: 'Turns on and off frequently (short cycling)', icon: RestartAltIcon },
  { id: 'pilot-light', label: 'Pilot light keeps going out', icon: LocalFireDepartmentIcon },
  { id: 'no-heat-one-room', label: 'No heat in one room or zone', icon: MeetingRoomIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const HEAT_TYPE_OPTIONS = [
  { id: 'gas-furnace', label: 'Gas furnace', icon: LocalFireDepartmentIcon },
  { id: 'electric-furnace', label: 'Electric furnace', icon: ElectricalServicesIcon },
  { id: 'heat-pump', label: 'Heat pump', icon: EnergySavingsLeafIcon },
  { id: 'boiler', label: 'Boiler / radiant', icon: WhatshotIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const HEAT_TRIED_OPTIONS = [
  { id: 'checked-thermostat', label: 'Yes, I checked the thermostat settings', icon: ThermostatIcon },
  { id: 'checked-filter', label: 'Yes, I checked or replaced the filter', icon: FilterAltIcon },
  { id: 'reset-breaker', label: 'Yes, I reset the breaker', icon: RestartAltIcon },
  { id: 'no-try', label: "No, I haven't tried anything", icon: SentimentNeutralIcon },
  { id: 'not-comfortable', label: "I'm not comfortable trying", icon: HelpOutlineIcon },
];

// ─── Smell or Sound ─────────────────────────────────────────────────────────

const SMELL_SOUND_SYMPTOM_OPTIONS = [
  { id: 'burning-smell', label: 'Burning smell when system runs', icon: LocalFireDepartmentIcon },
  { id: 'musty-smell', label: 'Musty or moldy smell from vents', icon: AirIcon },
  { id: 'rotten-egg', label: 'Rotten egg / sulfur smell (possible gas leak)', icon: GasMeterIcon, urgent: true },
  { id: 'loud-banging', label: 'Loud banging or popping noises', icon: VolumeUpIcon },
  { id: 'squealing', label: 'Squealing or screeching sounds', icon: HearingIcon },
  { id: 'rattling', label: 'Rattling or vibrating', icon: SensorsIcon },
  { id: 'clicking', label: "Clicking but system won't start", icon: PowerOffIcon },
  { id: 'not-sure', label: 'Not sure / other', icon: HelpOutlineIcon },
];

const SMELL_SOUND_WHEN_OPTIONS = [
  { id: 'every-time', label: 'Every time the system runs', icon: RestartAltIcon },
  { id: 'startup-only', label: 'Only when it first starts up', icon: BoltIcon },
  { id: 'intermittent', label: 'Comes and goes randomly', icon: TimerIcon },
  { id: 'just-started', label: 'Just started today', icon: NewReleasesIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

// ─── Airflow / Vents ────────────────────────────────────────────────────────

const AIRFLOW_SYMPTOM_OPTIONS = [
  { id: 'weak-airflow', label: 'Very weak airflow from vents', icon: SpeedIcon },
  { id: 'no-airflow', label: 'No air coming out at all', icon: BlockIcon },
  { id: 'uneven', label: 'Some rooms hot, others cold', icon: SwapHorizIcon },
  { id: 'one-vent', label: "One specific vent isn't working", icon: AirIcon },
  { id: 'dust-debris', label: 'Excessive dust or debris from vents', icon: FilterAltIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const AIRFLOW_LOCATION_OPTIONS = [
  { id: 'whole-house', label: 'Whole house', icon: HomeIcon },
  { id: 'upstairs', label: 'Upstairs only', icon: CabinIcon },
  { id: 'downstairs', label: 'Downstairs only', icon: HomeIcon },
  { id: 'one-room', label: 'One specific room', icon: MeetingRoomIcon },
  { id: 'multiple-rooms', label: 'A few specific rooms', icon: FormatListNumberedIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

// ─── Tune-up / Maintenance ──────────────────────────────────────────────────

const TUNEUP_SYSTEM_OPTIONS = [
  { id: 'ac-only', label: 'Air conditioning only', icon: AcUnitIcon },
  { id: 'heating-only', label: 'Heating only', icon: WhatshotIcon },
  { id: 'both', label: 'Both heating and cooling', icon: SwapHorizIcon },
  { id: 'not-sure', label: 'Not sure - tech can advise', icon: HelpOutlineIcon },
];

const TUNEUP_LAST_SERVICE_OPTIONS = [
  { id: 'under-1-year', label: 'Within the last year', icon: EventAvailableIcon },
  { id: '1-to-2-years', label: '1-2 years ago', icon: CalendarMonthIcon },
  { id: 'over-2-years', label: 'Over 2 years ago', icon: HistoryIcon },
  { id: 'never', label: 'Never / not sure', icon: HelpOutlineIcon },
];

const TUNEUP_TIMING_OPTIONS = [
  { id: 'before-summer', label: 'Before summer (AC prep)', icon: WbSunnyIcon },
  { id: 'before-winter', label: 'Before winter (heating prep)', icon: NightsStayIcon },
  { id: 'flexible', label: "I'm flexible on timing", icon: DateRangeIcon },
  { id: 'asap', label: 'As soon as possible', icon: WarningAmberIcon },
];

// ─── Thermostat Install ─────────────────────────────────────────────────────

const THERMOSTAT_NEED_OPTIONS = [
  { id: 'replace', label: 'Replace an existing thermostat', icon: SwapHorizIcon },
  { id: 'install-new', label: "Install one where there isn't one", icon: AddCircleOutlineIcon },
  { id: 'upgrade-smart', label: 'Upgrade to a smart thermostat', icon: SmartToyIcon },
  { id: 'not-working', label: "My thermostat isn't working right", icon: BuildIcon },
  { id: 'not-sure', label: 'Not sure - need a recommendation', icon: HelpOutlineIcon },
];

const THERMOSTAT_TYPE_OPTIONS = [
  { id: 'basic', label: 'Basic programmable thermostat', icon: TuneIcon },
  { id: 'smart-wifi', label: 'Smart / Wi-Fi thermostat (Nest, Ecobee, etc.)', icon: WifiIcon },
  { id: 'no-preference', label: 'No preference - I need advice', icon: HelpOutlineIcon },
];

const THERMOSTAT_PURCHASED_OPTIONS = [
  { id: 'yes', label: 'Yes, I already have it', icon: CheckIcon },
  { id: 'need-help', label: 'No, I need help choosing', icon: SearchIcon },
  { id: 'deciding', label: "I'm still deciding", icon: HelpOutlineIcon },
];

// ─── AC Install ─────────────────────────────────────────────────────────────

const AC_INSTALL_REASON_OPTIONS = [
  { id: 'replacing-old', label: 'Replacing an old or broken unit', icon: SwapHorizIcon },
  { id: 'first-time', label: 'First-time AC installation', icon: AddCircleOutlineIcon },
  { id: 'adding-zone', label: 'Adding AC to a new area or zone', icon: HomeIcon },
  { id: 'upgrade', label: 'Upgrading to a more efficient system', icon: EnergySavingsLeafIcon },
  { id: 'remodel', label: 'Part of a remodel or new construction', icon: ConstructionIcon },
  { id: 'exploring', label: 'Just exploring options and pricing', icon: SearchIcon },
];

const AC_INSTALL_TYPE_OPTIONS = [
  { id: 'central', label: 'Central air conditioning', icon: HvacIcon },
  { id: 'mini-split', label: 'Ductless mini-split', icon: AcUnitIcon },
  { id: 'heat-pump', label: 'Heat pump (heating and cooling)', icon: EnergySavingsLeafIcon },
  { id: 'window-unit', label: 'Window unit installation', icon: KitchenIcon },
  { id: 'not-sure', label: 'Not sure - need a recommendation', icon: HelpOutlineIcon },
];

const AC_INSTALL_HOME_OPTIONS = [
  { id: 'has-ducts', label: 'Yes, my home has ductwork', icon: AirIcon },
  { id: 'no-ducts', label: 'No ductwork', icon: BlockIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

// ─── Furnace / Heating Install ──────────────────────────────────────────────

const FURNACE_INSTALL_REASON_OPTIONS = [
  { id: 'replacing-old', label: 'Replacing an old or broken furnace', icon: SwapHorizIcon },
  { id: 'first-time', label: 'First-time heating installation', icon: AddCircleOutlineIcon },
  { id: 'switch-fuel', label: 'Switching fuel types (gas to electric or vice versa)', icon: SwapHorizIcon },
  { id: 'upgrade', label: 'Upgrading to a more efficient system', icon: EnergySavingsLeafIcon },
  { id: 'remodel', label: 'Part of a remodel or new construction', icon: ConstructionIcon },
  { id: 'exploring', label: 'Just exploring options and pricing', icon: SearchIcon },
];

const FURNACE_INSTALL_TYPE_OPTIONS = [
  { id: 'gas-furnace', label: 'Gas furnace', icon: LocalFireDepartmentIcon },
  { id: 'electric-furnace', label: 'Electric furnace', icon: ElectricalServicesIcon },
  { id: 'heat-pump', label: 'Heat pump', icon: EnergySavingsLeafIcon },
  { id: 'boiler', label: 'Boiler system', icon: WhatshotIcon },
  { id: 'not-sure', label: 'Not sure - need a recommendation', icon: HelpOutlineIcon },
];

// ─── Emergency (wizard-only; skips urgency — goes to photos after details) ─

const EMERGENCY_OPTIONS = [
  { id: 'no-heat-freezing', label: 'No heat and temperatures are freezing', icon: SevereColdIcon, urgent: true },
  { id: 'no-ac-extreme', label: 'No AC and extreme heat (health concern)', icon: WhatshotIcon, urgent: true },
  { id: 'gas-smell', label: 'Gas smell or carbon monoxide alarm', icon: GasMeterIcon, urgent: true },
  { id: 'system-smoking', label: 'System is smoking or sparking', icon: LocalFireDepartmentIcon, urgent: true },
  { id: 'flooding-from-unit', label: 'Water flooding from the HVAC unit', icon: WaterDropIcon, urgent: true },
  { id: 'other', label: 'Other urgent issue', icon: ErrorOutlineIcon, urgent: true },
];

const EMERGENCY_WHO_OPTIONS = [
  { id: 'elderly', label: 'Elderly person in the home', icon: HelpOutlineIcon },
  { id: 'children', label: 'Young children or infants', icon: HelpOutlineIcon },
  { id: 'medical', label: 'Someone with a medical condition', icon: ReportProblemIcon },
  { id: 'none', label: 'No vulnerable individuals', icon: CheckIcon },
];

// ─── Flow branch definitions ────────────────────────────────────────────────

const FLOW_BRANCHES = {
  'ac-not-cooling': [
    { key: 'acSymptom', title: "What's happening with your AC?", subtitle: 'Pick the description that fits best.', icon: AcUnitIcon, options: AC_SYMPTOM_OPTIONS },
    { key: 'acAge', title: 'How old is your AC unit?', subtitle: 'This helps the tech know what to expect.', icon: HistoryIcon, options: AC_AGE_OPTIONS },
    { key: 'acFilter', title: 'Have you changed the air filter recently?', subtitle: 'A dirty filter is the most common cause of AC issues.', icon: FilterAltIcon, options: AC_FILTER_OPTIONS },
  ],
  'heat-not-working': [
    { key: 'heatSymptom', title: "What's going on with your heating?", subtitle: 'Pick the description that fits best.', icon: WhatshotIcon, options: HEAT_SYMPTOM_OPTIONS },
    { key: 'heatType', title: 'What type of heating system do you have?', subtitle: 'This helps us match the right technician.', icon: SettingsIcon, options: HEAT_TYPE_OPTIONS },
    { key: 'heatTried', title: 'Have you tried anything?', subtitle: 'No wrong answer - this helps the tech come prepared.', icon: HandymanIcon, options: HEAT_TRIED_OPTIONS },
  ],
  'smell-sound': [
    { key: 'smellSoundSymptom', title: 'What are you noticing?', subtitle: 'Select the description that fits best.', icon: HearingIcon, options: SMELL_SOUND_SYMPTOM_OPTIONS },
    { key: 'smellSoundWhen', title: 'When does it happen?', subtitle: 'Timing helps the tech narrow down the cause.', icon: TimerIcon, options: SMELL_SOUND_WHEN_OPTIONS },
  ],
  'airflow-vents': [
    { key: 'airflowSymptom', title: "What's going on with the airflow?", subtitle: 'Pick the description that fits best.', icon: AirIcon, options: AIRFLOW_SYMPTOM_OPTIONS },
    { key: 'airflowLocation', title: 'Where is the problem?', subtitle: 'This helps the tech identify ductwork or zone issues.', icon: HomeIcon, options: AIRFLOW_LOCATION_OPTIONS },
  ],
  'tune-up': [
    { key: 'tuneupSystem', title: 'Which system needs service?', subtitle: "Select what you'd like maintained.", icon: BuildIcon, options: TUNEUP_SYSTEM_OPTIONS },
    { key: 'tuneupLastService', title: 'When was your last maintenance visit?', subtitle: 'This helps the tech know what to prioritize.', icon: CalendarMonthIcon, options: TUNEUP_LAST_SERVICE_OPTIONS },
    { key: 'tuneupTiming', title: 'When would you like the visit?', subtitle: 'Seasonal tune-ups prevent costly breakdowns.', icon: ScheduleIcon, options: TUNEUP_TIMING_OPTIONS },
  ],
  'thermostat-install': [
    { key: 'thermostatNeed', title: 'What do you need?', subtitle: 'Let us know what kind of thermostat work you need.', icon: ThermostatIcon, options: THERMOSTAT_NEED_OPTIONS },
    { key: 'thermostatType', title: 'What type of thermostat?', subtitle: 'This helps us match you with the right installer.', icon: DeviceThermostatIcon, options: THERMOSTAT_TYPE_OPTIONS },
    { key: 'thermostatPurchased', title: 'Do you have the thermostat already?', subtitle: 'This helps the tech know whether to bring one.', icon: CheckIcon, options: THERMOSTAT_PURCHASED_OPTIONS },
  ],
  'ac-install': [
    { key: 'acInstallReason', title: "What's prompting this?", subtitle: 'This helps us find the right installer for your situation.', icon: SearchIcon, options: AC_INSTALL_REASON_OPTIONS },
    { key: 'acInstallType', title: 'What type of AC system?', subtitle: "If you're not sure, the installer can help you decide.", icon: AcUnitIcon, options: AC_INSTALL_TYPE_OPTIONS },
    { key: 'acInstallHome', title: 'Does your home have existing ductwork?', subtitle: 'This affects which systems will work best.', icon: AirIcon, options: AC_INSTALL_HOME_OPTIONS },
  ],
  'furnace-install': [
    { key: 'furnaceInstallReason', title: "What's prompting this?", subtitle: 'This helps installers prepare the most accurate quote.', icon: SearchIcon, options: FURNACE_INSTALL_REASON_OPTIONS },
    { key: 'furnaceInstallType', title: 'What type of heating system?', subtitle: 'Not sure? The installer can recommend one.', icon: WhatshotIcon, options: FURNACE_INSTALL_TYPE_OPTIONS },
  ],
  emergency: [
    { key: 'emergencyType', title: "What's the emergency?", subtitle: "Tell us what's happening so we can act fast.", icon: ReportProblemIcon, options: EMERGENCY_OPTIONS },
    { key: 'emergencyWho', title: 'Is anyone vulnerable in the home?', subtitle: 'This helps us prioritize your request appropriately.', icon: HomeIcon, options: EMERGENCY_WHO_OPTIONS },
  ],
};

const SHARED_TAIL_KEYS = ['urgency', 'photos', 'address', 'review'];
const EMERGENCY_TAIL_KEYS = ['photos', 'address', 'review'];

const STEP_LABELS = {
  problem: {
    title: 'What do you need help with?',
    subtitle: 'Select the issue that best describes your heating or cooling need.',
    icon: HvacIcon,
  },
  urgency: {
    title: 'When do you need help?',
    subtitle: 'Let us know your timeline so we can prioritize.',
    icon: ScheduleIcon,
  },
  photos: {
    title: 'Photos & details',
    subtitle: 'Optional - but photos help HVAC techs give better estimates.',
    icon: AddPhotoAlternateIcon,
  },
  address: {
    title: 'What city do you live in?',
    subtitle:
      'Enter your city and state so we can match you with nearby pros. No street address needed.',
    icon: HomeIcon,
  },
  review: {
    title: 'Review your request',
    subtitle: "Make sure everything looks good before we find your HVAC pro.",
    icon: CheckCircleOutlineIcon,
  },
};

const ILLUSTRATION_CAPTIONS = {
  problem: "Tell us what's going on and we'll connect you with the right local HVAC technician.",
  acSymptom: 'Different AC problems need different fixes - this helps us narrow it down.',
  acAge: 'Older units may need different parts or may be more cost-effective to replace.',
  acFilter: 'A clogged filter is the #1 cause of AC problems - and the easiest fix.',
  heatSymptom: 'Knowing the symptom helps the tech diagnose the issue before they arrive.',
  heatType: 'Different systems require different expertise - this helps us match the right pro.',
  heatTried: 'No judgment - this helps the technician come prepared with the right tools.',
  smellSoundSymptom: 'Strange smells or sounds can signal anything from a loose part to a safety issue.',
  smellSoundWhen: "When it happens tells us a lot about what's causing it.",
  airflowSymptom: 'Weak or uneven airflow can mean ductwork issues, a failing blower, or simple fixes.',
  airflowLocation: 'If only certain areas are affected, it may point to a ductwork or zone issue.',
  tuneupSystem: 'Regular maintenance prevents 95% of HVAC breakdowns.',
  tuneupLastService: 'Systems serviced annually last significantly longer.',
  tuneupTiming: 'Getting ahead of the season means shorter wait times and better pricing.',
  thermostatNeed: 'A properly functioning thermostat is key to comfort and energy savings.',
  thermostatType: 'Smart thermostats can save up to 10-15% on heating and cooling costs.',
  thermostatPurchased: 'If you already have your thermostat, the tech just needs to install it.',
  acInstallReason: 'Understanding why helps installers prepare the most accurate quote.',
  acInstallType: 'Not sure which type is best for your home? Your installer can help you decide.',
  acInstallHome: 'Existing ductwork affects your options - ductless systems work great without it.',
  furnaceInstallReason: 'This helps us match you with an installer experienced in your situation.',
  furnaceInstallType: 'Each type has pros and cons - your installer can walk you through them.',
  emergencyType: "We're prioritizing your request - hang tight.",
  emergencyWho: 'Vulnerable individuals may need priority service - we want to keep everyone safe.',
  urgency: 'We prioritize urgent requests and match availability to your timeline.',
  photos: 'A picture is worth a thousand words - especially for HVAC issues.',
  address: "We'll use your city and state to find technicians who service your area.",
  review: "Almost there! Review your info and we'll find the best local pros.",
};

/** HVAC question config (saved-request display; see wizardServiceRegistry) */
export const hvacSubserviceFlowConfig = {
  problemStep: {
    options: HVAC_PROBLEMS,
    ...STEP_LABELS.problem,
  },
  flowBranches: FLOW_BRANCHES,
  sharedTailKeys: SHARED_TAIL_KEYS,
  urgentTailKeys: EMERGENCY_TAIL_KEYS,
  urgentProblemId: 'emergency',
  urgencyStep: {
    options: URGENCY_OPTIONS,
    ...STEP_LABELS.urgency,
  },
  photosStep: STEP_LABELS.photos,
  addressStep: STEP_LABELS.address,
  reviewStep: STEP_LABELS.review,
  illustrationCaptions: ILLUSTRATION_CAPTIONS,
  submitButtonLabel: 'Find my HVAC pro',
};